import {useMemo, useState} from 'react'
import {usePolicyCatalog} from '@/hooks/usePolicyCatalog'
import {Loader} from '@/customComponents/uiComponents/Loader.jsx'
import {supportingPerson} from '@img/index.js'

/**
 * The /help answer to "What is hop'n's cancellation, modification, and refund policy?",
 * built entirely from `/config/policies`. It replaced a hardcoded three-band mock.
 *
 * Layout: the three SECTIONS run down the left; the panel on the right carries the two
 * nested tab rows the data actually has — direction, then distance band — and lists that
 * band's tiers.
 *
 *   Cancellation ─┐   [ From Airport | To Airport | Others ]
 *   Modification  ├─► [ Up to 70 km  | Over 70 km          ]
 *   Refund       ─┘   $0   Free cancellation
 *                          More than 3 hours before pickup
 *
 * Why the nesting is in that order: a band only means something inside a direction (the
 * from-airport set has one open-ended band, to-airport has two), so direction is the
 * outer choice and the band list is rebuilt from whichever direction is selected.
 *
 * REFUND HAS NO ARRAY OF ITS OWN — it is the cancellation tiers read from the other side:
 * a tier's `flat_fee_cents` is what you are charged, `refund_percentage` is what comes
 * back. Same rows, different column, which is exactly how the CMR popup already treats it.
 */

const SECTIONS = [
    {key: 'cancellation', label: 'Cancellation'},
    {key: 'modification', label: 'Modification'},
    {key: 'refund', label: 'Refund'},
]

// cents → "$0" / "$25" / "$25.50" (same rule as the CMR popup).
const formatFeeCents = (cents) => {
    const dollars = (cents ?? 0) / 100
    return `$${dollars % 1 === 0 ? dollars : dollars.toFixed(2)}`
}

/**
 * "Up to 70 km" / "Over 70 km" / "20 – 50 km" / "All distances".
 * `to_km: null` means open-ended, and a 0 → null band covers everything — that one gets
 * a name rather than "Over 0 km", which would read as a restriction that isn't there.
 */
const rangeLabel = ({from_km: from, to_km: to} = {}) => {
    const f = Number(from) || 0
    if (to == null) return f > 0 ? `Over ${f} km` : 'All distances'
    if (f <= 0) return `Up to ${to} km`
    return `${f} – ${to} km`
}

// Tiers arrive newest-band-first in the payload; the passenger reads them as a timeline,
// so they're shown longest-notice first (the free tier at the top, like the design).
const orderedTiers = (tiers = []) => [...tiers].sort((a, b) => (b.band ?? 0) - (a.band ?? 0))

/** One row: big value on the left, title + the API's own `label` underneath. */
// Same shape as the CMR popup's TierRow, `qualifier` included — /help and the booking
// modal must render a tier identically.
function TierRow({value, title, qualifier, subtitle}) {
    return (
        <div className="hopn-cmr-tier">
            <span className="hopn-cmr-tier-value">{value}</span>
            <div className="hopn-cmr-tier-text">
                <h5>{title}{qualifier && <span className="hopn-cmr-tier-qual"> {qualifier}</span>}</h5>
                {subtitle && <p className="mb-0!">{subtitle}</p>}
            </div>
        </div>
    )
}

function Note({icon, title, children}) {
    return (
        <div className="hopn-cmr-note">
            {title && (
                <div className="hopn-cmr-note-head">
                    {icon && <img src={icon} alt=""/>}
                    <h6>{title}</h6>
                </div>
            )}
            {children}
        </div>
    )
}

/** Pill tab row — used for both the direction and the distance-band levels. */
function TabRow({items, active, onSelect, size = 'md'}) {
    if (items.length <= 1 && size === 'sm') return null   // a lone band is not a choice
    return (
        // Mobile: ONE line that scrolls horizontally, so adding policy types later never
        // wraps the row into a second (or third) line. From md up it wraps as before,
        // where there is width for it. `shrink-0` stops the pills being squeezed
        // narrower than their label instead of overflowing into the scroll.
        <div className="hopn-policy-tabs flex gap-2 flex-nowrap overflow-x-auto md:flex-wrap md:overflow-x-visible">
            {items.map((it) => (
                <button
                    key={it.key}
                    type="button"
                    onClick={() => onSelect(it.key)}
                    className={`cursor-pointer border-0 rounded-full font-semibold shrink-0 ${
                        size === 'sm' ? 'px-3.5 py-1.5 text-xs' : 'px-4 py-2 text-sm'
                    } ${it.key === active
                        ? 'bg-(--text-primary) text-white'
                        : 'bg-(--bg-grey) text-(--text-primary)'}`}
                >
                    {it.label}
                </button>
            ))}
        </div>
    )
}

export default function PolicyCatalog() {
    const {data: catalog, isLoading, isError, refetch} = usePolicyCatalog()

    const [section, setSection] = useState('cancellation')
    const [direction, setDirection] = useState(null)   // null = "first available"
    const [bandIdx, setBandIdx] = useState(0)

    // Refund reads the cancellation tree — it has no directions array of its own.
    const tree = section === 'modification' ? catalog?.modification : catalog?.cancellation
    const directions = useMemo(() => tree?.directions || [], [tree])

    // Falling back to the first entry keeps the panel populated when a section doesn't
    // carry the direction that was selected in another one.
    const activeDirection = directions.find((d) => d.direction === direction) || directions[0] || null
    const bands = activeDirection?.distance_bands || []
    const activeBand = bands[bandIdx] || bands[0] || null

    const selectDirection = (key) => {
        setDirection(key)
        setBandIdx(0)   // band lists differ per direction; index 0 is the only safe carry-over
    }

    if (isLoading) {
        return <div className="flex justify-center py-10"><Loader color="#201e20"/></div>
    }

    if (isError || !catalog) {
        return (
            <div className="py-6 text-center">
                <p className="mb-4 text-(--text-secondary)">We couldn't load the policies just now.</p>
                <button
                    type="button"
                    onClick={() => refetch()}
                    className="cursor-pointer rounded-full border-0 bg-(--text-primary) px-6 py-2.5 text-white text-sm font-semibold"
                >
                    Try again
                </button>
            </div>
        )
    }

    return (
        // .hopn-cmr is what carries the tier + note styling (big value on the left,
        // divider between rows, grey callout) — those rules are all nested under it in
        // the stylesheet, so without this wrapper the rows render as plain text.
        <div className="hopn-cmr flex flex-col gap-5 md:flex-row md:gap-6">
            {/* Sections — a column on desktop, a pill row on mobile where a sidebar
                would eat most of the width. */}
            {/* Mobile: its own horizontal slider, same as the two TabRow rows below — one
                line that scrolls, so more sections later never wrap. This row does NOT go
                through TabRow (desktop turns it into the left sidebar column), so the
                slider classes are repeated here rather than shared. */}
            <div className="hopn-policy-tabs flex flex-row gap-2 flex-nowrap overflow-x-auto md:w-44 md:shrink-0 md:flex-col md:overflow-x-visible">
                {SECTIONS.map((s) => (
                    <button
                        key={s.key}
                        type="button"
                        onClick={() => setSection(s.key)}
                        className={`shrink-0 cursor-pointer border-0 rounded-2xl px-4 py-3 text-sm font-semibold md:flex-none md:w-full md:text-left ${
                            s.key === section
                                ? 'bg-(--text-primary) text-white'
                                : 'bg-(--bg-grey) text-(--text-primary)'
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="min-w-0 flex-1">
                <div className="mb-4 flex flex-col gap-2.5">
                    <TabRow
                        items={directions.map((d) => ({key: d.direction, label: d.direction_label || d.direction}))}
                        active={activeDirection?.direction}
                        onSelect={selectDirection}
                    />
                    <TabRow
                        size="sm"
                        items={bands.map((b, i) => ({key: i, label: rangeLabel(b.range_km)}))}
                        active={bands.indexOf(activeBand)}
                        onSelect={setBandIdx}
                    />
                </div>

                {section === 'modification'
                    ? <ModificationBand direction={activeDirection} band={activeBand}/>
                    : <CancellationBand band={activeBand} refund={section === 'refund'}/>}
            </div>
        </div>
    )
}

/**
 * Cancellation and Refund render the SAME tiers from opposite sides:
 *  - cancellation → what you're charged (`flat_fee_cents`)
 *  - refund       → what comes back (`refund_percentage`)
 */
function CancellationBand({band, refund}) {
    if (!band) return null
    const tiers = orderedTiers(band.tiers)

    // No tiers is meaningful, not "still loading": the backend imposes no bands here, so
    // the action is unconditionally free / fully refundable.
    if (!tiers.length) {
        // FREE_FALLBACK from the CMR popup, qualifier included — a 100% refund is the one
        // case where the card is never touched, and the popup says so.
        return (
            <TierRow
                value={refund ? '100%' : '$0'}
                title={refund ? 'Full refund' : 'Free cancellation'}
                qualifier={refund ? '(No Charge on your Card)' : undefined}
                subtitle={refund ? 'You get your full fare back whenever you cancel' : 'You can cancel your booking any time'}
            />
        )
    }

    return (
        <>
            {tiers.map((t, i) => {
                if (!refund) {
                    return (
                        <TierRow
                            key={i}
                            value={formatFeeCents(t.flat_fee_cents)}
                            title={t.is_free ? 'Free cancellation' : 'Late cancellation fee'}
                            subtitle={t.label}
                        />
                    )
                }
                // Refund rules, identical to the CMR popup: a tier expresses its refund as
                // EITHER a flat fee deducted ($X) or a percentage returned (Y%). Show the
                // flat fee when the API sets one (> 0), else the percentage. A flat fee OR
                // a partial refund both mean the card WAS charged — only a 100% refund
                // leaves it untouched, which is what earns the qualifier.
                const cents = t.flat_fee_cents
                const hasFlatFee = typeof cents === 'number' && cents > 0
                const pct = t.refund_percentage ?? 0
                const isFullRefund = !hasFlatFee && pct >= 100
                const isNoRefund = !hasFlatFee && pct <= 0
                return (
                    <TierRow
                        key={i}
                        value={hasFlatFee ? formatFeeCents(cents) : `${pct}%`}
                        title={isFullRefund ? 'Full refund' : isNoRefund ? 'No refund' : 'Charge'}
                        qualifier={isFullRefund ? '(No Charge on your Card)' : undefined}
                        subtitle={t.label}
                    />
                )
            })}

            {/* A no-show is an outcome of the cancellation policy, so it closes both tabs. */}
            {band.no_show?.text && (
                <Note icon={supportingPerson} title="If you don't show up">
                    <p className="mb-0!">{band.no_show.text}</p>
                </Note>
            )}
        </>
    )
}

/**
 * Modification has no per-tier fee — its numbers are the free-change allowance and how
 * late a change is still accepted, both of which live on the direction/band rather than
 * on tiers. `tiers` is empty in every direction today; when the backend starts filling
 * it, those rows render above the summary instead of replacing it.
 */
function ModificationBand({direction, band}) {
    if (!direction || !band) return null
    const free = Number(direction.free_modifications) || 0
    const hours = Number(band.editable_hours_before_pickup) || 0
    const tiers = orderedTiers(band.tiers)

    return (
        <>
            {tiers.map((t, i) => (
                <TierRow
                    key={i}
                    value={t.flat_fee_cents ? formatFeeCents(t.flat_fee_cents) : '$0'}
                    title={t.can_edit === false ? 'No changes allowed' : 'Changes allowed'}
                    subtitle={t.label}
                />
            ))}

            {!tiers.length && (
                free > 0 ? (
                    <TierRow
                        value={String(free)}
                        title="Free changes"
                        // The band's own sentence when there is one — it already states the
                        // allowance and the cut-off in the backend's wording, so repeating
                        // it in a note underneath just said the same thing twice.
                        subtitle={band.text || (hours > 0
                            ? `You can change your booking up to ${hours} ${hours === 1 ? 'hour' : 'hours'} before pickup.`
                            : "Contact our support team and we'll make the change for you.")}
                    />
                ) : (
                    // NO tiers and no configured allowance means the backend imposes no
                    // modification bands here — NOT that changes are refused. Building a
                    // row out of the zeroed fields produced "0 / No free changes / You have
                    // 0 free changes … up to 0 hours before pickup", which reads as a
                    // restriction that doesn't exist. Use the same free-changes fallback
                    // the CMR popup shows (FREE_FALLBACK.modificationBooking), so /help and
                    // the booking flow tell the passenger the same thing.
                    <TierRow
                        value="$0"
                        title="Free changes"
                        subtitle='Yes, you can modify your booking at no extra cost. Just go to the "Modify" section after booking and connect with our support team to make changes.'
                    />
                )
            )}
        </>
    )
}
